import { useEffect } from 'react';
import { Platform } from 'react-native';

/**
 * Keyframes and selectors that cannot be expressed as inline styles.
 *
 * These are keyed off `data-*` attributes rather than class names: React Native
 * Web does not forward `className` on `View`/`ScrollView`, so class-based rules
 * never match. `dataSet` is the supported escape hatch — it maps to `data-*`.
 */
const LANDING_CSS = `
  [data-scene-active="false"] * { animation-play-state: paused !important; }
  @keyframes moment-steam {
    0% { opacity: 0; transform: translate(0, 0); }
    20% { opacity: .28; }
    100% { opacity: 0; transform: translate(7px, -24px); }
  }
  [data-moment-steam] { animation: moment-steam 6.5s linear infinite; }
  @keyframes moment-leaf {
    0% { opacity: 0; transform: translate(0, 0) rotate(0deg); }
    15%, 65% { opacity: .4; }
    45% { transform: translate(22px, 65px) rotate(45deg); }
    100% { opacity: 0; transform: translate(-10px, 170px) rotate(110deg); }
  }
  [data-moment-leaf] { transform-box: fill-box; transform-origin: center; animation: moment-leaf 20s linear infinite; }
  @keyframes moment-water { to { stroke-dashoffset: -20; } }
  [data-moment-water] { animation: moment-water 1.3s linear infinite; }
  /* Animate individual pixel details, never pan or zoom the base artwork. */
  @keyframes pixel-ripple {
    0%, 100% { opacity: .06; transform: translateX(-2px) scaleX(.95); }
    50% { opacity: .32; transform: translateX(2px) scaleX(1.05); }
  }
  [data-pixel-ripple] {
    transform-box: fill-box;
    transform-origin: center;
    animation: pixel-ripple 5s ease-in-out infinite;
  }
  @keyframes pixel-leaf {
    0% { opacity: 0; transform: translate(0, 0) rotate(0deg); }
    10%, 70% { opacity: .45; }
    45% { transform: translate(-20px, 65px) rotate(45deg); }
    100% { opacity: 0; transform: translate(-45px, 150px) rotate(100deg); }
  }
  [data-pixel-leaf] {
    transform-box: fill-box;
    transform-origin: center;
    animation: pixel-leaf 18s linear infinite;
  }
  @keyframes pixel-star { 0%, 100% { opacity: .15; } 50% { opacity: .95; } }
  [data-pixel-star] { animation: pixel-star 6s ease-in-out infinite; }
  @keyframes pixel-lantern { 0%, 100% { opacity: .45; } 50% { opacity: .9; } }
  [data-pixel-lantern] { animation: pixel-lantern 4s ease-in-out infinite; }
  @keyframes pixel-firefly {
    0%, 100% { opacity: 0; transform: translate(0, 0); }
    35% { opacity: .45; }
    70% { opacity: .4; transform: translate(-18px, -28px); }
  }
  [data-pixel-firefly] { animation: pixel-firefly 10s ease-in-out infinite; }
  [data-landing-bot-choice] { transition: background-color 160ms ease, border-color 160ms ease; }
  [data-landing-preview-choice]:focus-visible,
  [data-landing-scroll] a:focus-visible,
  [data-landing-scroll] [role="button"]:focus-visible,
  [data-landing-bot-choice]:focus-visible {
    outline: 2px solid #040404 !important;
    outline-offset: 2px;
  }

  [data-landing-theme="dark"] [data-landing-preview-choice]:focus-visible,
  [data-landing-theme="dark"] [data-landing-bot-choice]:focus-visible,
  [data-landing-theme="dark"] a:focus-visible,
  [data-landing-theme="dark"] [role="button"]:focus-visible { outline-color: #FFFFFF !important; }

  [data-pricing-page] a:focus-visible,
  [data-pricing-page] button:focus-visible,
  [data-pricing-page] [role="button"]:focus-visible,
  [data-pricing-page] [role="radio"]:focus-visible {
    outline: 2px solid #8C8C8C !important;
    outline-offset: 4px;
  }

  [data-landing-scroll]::-webkit-scrollbar { width: 10px; }
  [data-landing-scroll]::-webkit-scrollbar-track { background: transparent; }
  [data-landing-scroll]::-webkit-scrollbar-thumb {
    background: rgba(150,150,150,0.22);
    border-radius: 999px;
    border: 3px solid transparent;
    background-clip: content-box;
  }
  [data-landing-scroll]::-webkit-scrollbar-thumb:hover { background: rgba(150,150,150,0.42); background-clip: content-box; }

  @keyframes landing-marquee {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  [data-landing-marquee] {
    animation: landing-marquee 44s linear infinite;
    will-change: transform;
  }
  [data-landing-marquee="reverse"] { animation-direction: reverse; }
  [data-landing-marquee]:hover { animation-play-state: paused; }

  /* Voice-mode level meter. Per-bar phase comes from an inline animation-delay. */
  @keyframes landing-wave {
    0%, 100% { transform: scaleY(0.28); }
    50% { transform: scaleY(1); }
  }
  [data-landing-wave] {
    animation: landing-wave 1.5s ease-in-out infinite;
    transform-origin: center;
    will-change: transform;
  }

  /* Typewriter caret in the Work console. */
  @keyframes landing-caret {
    0%, 45% { opacity: 1; }
    50%, 95% { opacity: 0; }
  }
  [data-landing-caret] { animation: landing-caret 1s steps(1) infinite; }

  /* Live status dot. */
  @keyframes landing-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.6); opacity: 0.35; }
  }
  [data-landing-pulse] {
    animation: landing-pulse 1.4s ease-in-out infinite;
    will-change: transform, opacity;
  }

  /* Spinner ring for the step in progress. */
  @keyframes landing-spin { to { transform: rotate(360deg); } }
  [data-landing-spin] { animation: landing-spin 900ms linear infinite; }

  /* Slow "thinking" shimmer across the run's progress bar. */
  @keyframes landing-shimmer {
    from { transform: translateX(-100%); }
    to { transform: translateX(250%); }
  }
  [data-landing-shimmer] { animation: landing-shimmer 1.8s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    [data-moment-steam], [data-moment-leaf] { opacity: 0; }
    [data-pixel-scene] * { animation: none !important; }
    [data-pixel-ripple] { opacity: .3; }
    [data-pixel-leaf] { opacity: 0; }
    [data-pixel-firefly] { opacity: 0; }
    [data-landing-marquee] { animation: none !important; }
    [data-landing-wave] { animation: none !important; transform: scaleY(0.7) !important; }
    [data-landing-caret],
    [data-landing-pulse],
    [data-landing-spin],
    [data-landing-shimmer] { animation: none !important; }
    [data-landing-scroll] * {
      transition-duration: 0.01ms !important;
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
    }
  }
`;

const STYLE_ID = 'landing-page-styles';

/**
 * Mounts the landing stylesheet for as long as a page that uses the landing kit
 * is on screen. Shared rather than per-page so `Marquee` and the reduced-motion
 * overrides behave the same everywhere the kit is used.
 */
export default function useLandingStyles() {
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    if (document.getElementById(STYLE_ID)) return undefined;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = LANDING_CSS;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);
}
